import type { CommutePlan, Coordinates } from "../shared/types";
import { distanceMeters } from "../shared/time";
import { BART_STATIONS } from "./profiles";
import type { CardStatus } from "./format";
import { CONTRADICTS_ARRIVAL_METERS } from "./journey";

const FRESH_FIX_MS = 30_000;
const USABLE_ACCURACY_METERS = 50;
const STATION_CONTAINMENT_METERS = 120;
const STOPPED_METERS_PER_SECOND = 1;

export interface OnboardPosition extends Coordinates {
	accuracyMeters: number | null;
	speedMetersPerSecond: number | null;
}

export interface OnboardStatusInput {
	plan: CommutePlan | null;
	destinationIndex: number;
	position: OnboardPosition | null;
	fixAtMs: number | null;
	nowMs: number;
}

/**
 * Replace an expired agency ETA only when the phone can contradict it directly.
 *
 * This does not estimate arrival. It reports the two facts GPS can establish: the train has not
 * reached the destination, and whether it is stopped at a named station or between stations.
 */
export function observedOnboardStatus(input: OnboardStatusInput): CardStatus | null {
	const { plan, destinationIndex, position, fixAtMs, nowMs } = input;
	const destination = plan?.destinations[destinationIndex] ?? plan?.destinations[0] ?? null;
	if (
		!plan ||
		!destination ||
		destination.predictedMs > nowMs ||
		!position ||
		fixAtMs == null ||
		nowMs - fixAtMs > FRESH_FIX_MS ||
		position.accuracyMeters == null ||
		position.accuracyMeters > USABLE_ACCURACY_METERS
	) {
		return null;
	}

	const destinationStation = BART_STATIONS[destination.stopId];
	if (destinationStation && distanceMeters(position, destinationStation) <= CONTRADICTS_ARRIVAL_METERS) {
		return null;
	}

	const stopped = position.speedMetersPerSecond != null && position.speedMetersPerSecond <= STOPPED_METERS_PER_SECOND;
	const routeStopIds = new Set([...plan.upstream, plan.boarding, ...plan.downstream].map((stop) => stop.stopId));
	const station = stopped
		? [...routeStopIds]
				.map((stopId) => BART_STATIONS[stopId])
				.filter((candidate) => candidate != null && candidate.id !== destination.stopId)
				.map((candidate) => ({ candidate, meters: distanceMeters(position, candidate) }))
				.filter(({ meters }) => meters + position.accuracyMeters! <= STATION_CONTAINMENT_METERS)
				.sort((left, right) => left.meters - right.meters)[0]?.candidate ?? null
		: null;

	const destinationName = destination.stopName.toUpperCase();
	return {
		lead: stopped ? (station ? `STOPPED AT ${station.name.toUpperCase()}` : "STOPPED") : `${destinationName} NEXT`,
		hero: "NO ETA",
		heroMeridiem: null,
		unit: null,
		sub: stopped ? `${destinationName} next` : "GPS shows the train is still en route.",
		tone: "info",
	};
}